<template>
  <div class="Users">
    <b-row class="mb-4 align-items-end">
      <b-col class="text-left">
        <h1>Users</h1>
      </b-col>

      <b-col cols="2" class="text-right"
        ><b-button
          variant="outline-primary"
          @click="openNewUserModal"
          :disabled="!canCreateUserTenant"
          ><i class="fas fa-user-plus"></i>&nbsp; Create a new user</b-button
        ></b-col
      >
    </b-row>
    <b-overlay :show="isLoading" spinner-variant="primary" opacity="1">
      <Table
        class="Users-list p-0 text-left"
        v-if="canSearchTenantUsers"
        filterable
        :items="users"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :total-rows="totalRows"
        :tableOptions="tableOptions"
        @filtered="onFilterChanged"
        @sort-changed="onSortChanged"
        @page-changed="onPageChanged"
        @per-page-changed="onPerPageChanged"
      >
        <template v-slot:cell(profiles)="data">
          <div v-for="tenant in data.item.tenants" :key="tenant.name">
            <template v-if="tenant.name === selectedTenant.name">
              <b-badge
                pill
                class="mr-1"
                variant="primary"
                v-for="profile in tenant.profiles"
                :key="`${tenant.name}-${profile}`"
                >{{ profile }}</b-badge
              >
            </template>
          </div>
        </template>

        <template v-slot:cell(actions)="data">
          <div class="text-center">
            <a
              href="#"
              title="Delete user"
              v-if="canDeleteUser"
              :data-cy="`delete-user-${data.item._id}`"
              @click="deleteUser(data.item._id)"
            >
              <i class="fas fa-trash" />
            </a>
            &nbsp;
            <a
              href="#"
              title="Edit user content"
              v-if="canAdministrateUserTenants"
              :data-cy="`view-user-content-${data.item._id}`"
              @click="showUserContentModal(data.item._id)"
            >
              <i class="fas fa-edit" /> </a
            >&nbsp;
            <a
              href="#"
              title="Manage user profiles"
              v-if="canAdministrateUserTenants"
              :data-cy="`view-user-tenant-${data.item._id}`"
              @click="showUserTenantModal(data.item._id)"
            >
              <i class="fas fa-user-cog" />
            </a>
          </div>
        </template>
        <template #empty>
          <h4 class="text-center mt-3">There is currently no users.</h4>
          <p class="text-center">
            Try changing the filters to modify your search.
          </p>
        </template>
      </Table>
      <ListNotAllowed v-else />
    </b-overlay>
    <user-profiles-modal
      v-if="currentViewedUser"
      :user="currentViewedUser"
      :profiles="profiles"
      :isLoading="isLoading"
      :selectedTenant="selectedTenant"
      @remove-profile="onRemoveProfile"
      @add-profile="onAddProfile"
      @modal-close="currentViewedUser = { _source: {} }"
    />
    <user-content-modal
      :user="currentViewedUser"
      :isLoading="isLoading"
      :profiles="profiles"
      :selectedTenant="selectedTenant"
      @create="onCreateUser"
      @update="onUpdateUser"
      @modal-close="currentViewedUser = { _source: {} }"
    />
  </div>
</template>

<script>
import UserProfilesModal from '@/components/UserProfilesModal';
import UserContentModal from '@/components/UserContentModal';
import ListNotAllowed from '@/components/ListNotAllowed';
import { mapGetters } from 'vuex';
import { isActionAllowed } from '@/helpers/rights.js';
import _ from 'lodash';

export default {
  name: 'Users',
  components: {
    UserContentModal,
    UserProfilesModal,
    ListNotAllowed
  },

  data() {
    return {
      rights: [],
      isLoading: false,
      users: [],
      profiles: [],
      userMapping: null,
      filter: null,
      minimumFilterLength: 3,
      currentViewedUser: { _source: {}, credentials: {} },
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      sort: null,
      tableOptions: {},
      fields: [
        {
          key: '_id',
          label: 'Id',
          sortable: true
        },
        {
          key: 'firstName',
          label: 'First name',
          sortable: true
        },
        {
          key: 'lastName',
          label: 'Last name',
          sortable: true
        },
        {
          key: 'email',
          label: 'Email',
          sortable: true
        },
        {
          key: 'phoneNumber',
          label: 'Phone number',
          sortable: true
        },
        {
          key: 'profiles',
          label: 'Profiles',
          sortable: false
        },
        {
          key: 'actions',
          label: '',
          sortable: false
        }
      ],
      filerableFields: [
        'lastName.text',
        'firstName.text',
        'email.text',
        'phoneNumber.text'
      ]
    };
  },

  computed: {
    ...mapGetters('tenants', ['tenants', 'selectedTenant']),
    ...mapGetters('auth', ['currentUserRights']),
    canSearchTenantUsers() {
      return (
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'search'
        ) &&
        isActionAllowed(this.currentUserRights, 'security', 'getUserMapping')
      );
    },
    canAdministrateUserTenants() {
      return (
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'addProfile'
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'listProfiles'
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'removeProfile'
        ) &&
        isActionAllowed(this.currentUserRights, 'multi-tenancy/user', 'get') &&
        isActionAllowed(this.currentUserRights, 'security', 'getUserMapping')
      );
    },
    canCreateUserTenant() {
      return (
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/user',
          'create'
        ) &&
        isActionAllowed(this.currentUserRights, 'security', 'getCredentials') &&
        isActionAllowed(this.currentUserRights, 'security', 'updateCredentials')
      );
    },
    canDeleteUser() {
      return isActionAllowed(
        this.currentUserRights,
        'multi-tenancy/user',
        'delete'
      );
    }
  },

  async mounted() {
    const promises = [];

    if (this.canSearchTenantUsers) {
      promises.push(this.fetchUserMapping(), this.fetchUsers());
    }

    if (this.canAdministrateUserTenants) {
      promises.push(this.fetchProfiles());
    }

    await Promise.all(promises);
  },

  methods: {
    async fetchUserMapping() {
      this.isLoading = true;

      try {
        const response = await this.$kuzzle.security.getUserMapping();
        this.userMapping = response.mapping;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async fetchUsers() {
      if (!this.selectedTenant) {
        return;
      }

      const users = [];
      const body = {
        query: {
          bool: {}
        }
      };

      body.sort = this.sort || { _id: { order: 'asc' } };

      if (this.filter) {
        body.query = {
          multi_match: {
            query: this.filter,
            type: 'phrase_prefix',
            fields: this.filerableFields
          }
        };
      }

      const request = {
        controller: 'multi-tenancy/user',
        action: 'search',
        tenantId: this.selectedTenant._id,
        from: (this.currentPage - 1) * this.perPage,
        size: this.perPage,
        lang: 'elasticsearch',
        body
      };

      try {
        this.isLoading = true;

        const response = await this.$kuzzle.query(request);

        this.totalRows = response.result.total;
        for (const user of response.result.hits) {
          users.push({
            _id: user._id,
            ...user._source
          });
        }

        this.users = users;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async fetchProfiles() {
      this.isLoading = true;

      try {
        const response = await this.$kuzzle.query({
          controller: 'multi-tenancy/user',
          action: 'listProfiles',
          tenantId: this.selectedTenant._id
        });

        this.profiles = response.result.profiles;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async onAddProfile(payload) {
      this.isLoading = true;
      try {
        const request = {
          controller: 'multi-tenancy/user',
          action: 'addProfile',
          ...payload
        };

        await this.$kuzzle.query(request);
        await this.fetchUsers();
        this.currentViewedUser = await this.getUser(this.currentViewedUser._id);
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async onRemoveProfile(payload) {
      this.isLoading = true;

      const tenant = this.tenants.find(el => el.name === payload.tenantName);

      if (!tenant) {
        const error = `Cannot find a tenant with the name "${payload.tenantName}".`;

        this.isLoading = false;
        this.$log.error(error);
        this.showErrorToast(error);
        return;
      }

      payload.tenantId = tenant._id;
      delete payload.tenantName;
      try {
        const request = {
          controller: 'multi-tenancy/user',
          action: 'removeProfile',
          ...payload
        };

        await this.$kuzzle.query(request);
        await this.fetchUsers();
        this.currentViewedUser = await this.getUser(this.currentViewedUser._id);
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async onFilterChanged(value) {
      this.filter = value;
      await this.fetchUsers();
    },
    async onSortChanged(value) {
      this.sort = {
        [value.sortBy]: { order: value.sortDesc ? 'desc' : 'asc' }
      };
      await this.fetchUsers();
    },
    async onPageChanged(value) {
      this.currentPage = value;
      await this.fetchUsers();
    },
    async onPerPageChanged(value) {
      this.perPage = value;
      await this.fetchUsers();
    },
    onTenantSelected(value) {
      this.selectedTenantId = value;
    },
    async onCreateUser(payload) {
      this.isLoading = true;

      try {
        await this.$kuzzle.query({
          controller: 'multi-tenancy/user',
          action: 'create',
          tenantId: payload.tenantId,
          profile: payload.profile,
          body: payload
        });

        await this.fetchUsers();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async onUpdateUser(payload) {
      this.isLoading = true;

      try {
        await this.$kuzzle.query({
          controller: 'multi-tenancy/user',
          action: 'update',
          tenantId: this.selectedTenant._id,
          _id: payload._id,
          body: payload._source
        });

        if (payload.credentials) {
          await this.$kuzzle.security.updateCredentials(
            'local',
            payload._id,
            payload.credentials
          );
        }

        await this.fetchUsers();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Sorry, an error has occurred.',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true,
        'auto-hide-delay': '10s'
      });
    },
    async getUser(userId) {
      try {
        const response = await this.$kuzzle.query({
          controller: 'multi-tenancy/user',
          action: 'get',
          tenantId: this.selectedTenant._id,
          _id: userId
        });

        return response.result;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }
    },
    async getUserCredentials(userId) {
      try {
        const credentials = await this.$kuzzle.security.getCredentials(
          'local',
          userId
        );

        return credentials;
      } catch (error) {
        // @todo : use error code instead of status
        // when https://github.com/kuzzleio/kuzzle/issues/1988 is resolved
        if (error.status === 412) {
          return null;
        }

        this.$log.error(error);
        this.showErrorToast(error.message);
      }
    },
    handleDeleteConfirmation(userId) {
      const customMessage = this.$createElement('p', [
        'Are you sure you want to delete the user ',
        this.$createElement('code', userId)
      ]);
      return this.$bvModal.msgBoxConfirm(customMessage);
    },
    async deleteUser(userId) {
      const isDeleteConfirmed = await this.handleDeleteConfirmation(userId);

      if (!isDeleteConfirmed) {
        return;
      }

      this.isLoading = true;

      try {
        await this.$kuzzle.query({
          controller: 'multi-tenancy/user',
          action: 'delete',
          tenantId: this.selectedTenant._id,
          _id: userId
        });

        await this.fetchUsers();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    openNewUserModal() {
      this.currentViewedUser = {
        content: {}
      };
      this.$bvModal.show('user-content-modal');
    },
    async showUserTenantModal(userId) {
      this.currentViewedUser = await this.getUser(userId);
      this.$bvModal.show('user-tenants-modal');
    },
    async showUserContentModal(userId) {
      const [user, credentials] = await Promise.all([
        this.getUser(userId),
        this.getUserCredentials(userId)
      ]);

      if (user && credentials) {
        this.currentViewedUser = user;
        this.currentViewedUser.credentials = credentials;

        this.$bvModal.show('user-content-modal');
      }
    }
  },

  watch: {
    selectedTenant() {
      if (this.canSearchTenantUsers) {
        this.fetchUsers();
        this.fetchProfiles();
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
